/*
 * Project: Dynamedics Portal Web
 * Created Date: Friday February 10th 2023
 * Author: KasunSKarunasekara
 * -----
 * Last Modified: Wednesday June 28th 2023 12:02:13 pm
 * Modified By: KasunSKarunasekara at <kkarunasekara@mitrai.com>
 * -----
 * Copyright (c) 2023 Mitra Sparks
 * -----
 * HISTORY:
 */

import { createSlice } from '@reduxjs/toolkit';
import { checkUndefinedOrNull } from 'src/utils/functions';
import axios from '../../utils/axios';

const initialState = {
  attachedSuppliers: [],
  supplierInvitations: [],
  searchedSuppliers: [],
  supplierApprovals: [],
  dataSubmissions: [],
  isAttachedSuppliersLoading: false,
  isSupplierInvitationLoading: false,
  isInvitationURLLoading: false,
  isApprovalsLoading: false,
  isSubmissionsLoading: false,
  supplierInvitationURL: '',
  isSearchSuppliersLoading: false,
  isSupplierDeleteLoading: false,
  invitedSuppliersCount: 0,
  addedSuppliersCount: 0,
  alreadyAttachedSuppliersCount: 0
};

const slice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    //START LOADING
    startLoading(state) {
      state.isLoading = true;
    },
    //HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      state.isAttachedSuppliersLoading = false;
      state.isSearchSuppliersLoading = false;
      state.isSupplierDeleteLoading = false;
      state.isSupplierInvitationLoading = false;
      state.isInvitationURLLoading = false;
      state.isApprovalsLoading = false;
      state.isSubmissionsLoading = false;
    },
    //START ATTACHED SUPPLIERS LOADING
    startAttachedSuppliersLoading(state) {
      state.isAttachedSuppliersLoading = true;
    },
    //START ATTACHED SUPPLIERS LOADING
    startSearchSuppliersLoading(state) {
      state.isSearchSuppliersLoading = true;
    },
    //START DELETE SUPPLIERS LOADING
    startDeleteSuppliersLoading(state) {
      state.isSupplierDeleteLoading = true;
    },
    //START INVITE SUPPLIERS LOADING
    startInviteSuppliersLoading(state) {
      state.isSupplierInvitationLoading = true;
    },
    //START INVITE URL LOADING
    startInvitationUrlLoading(state) {
      state.isInvitationURLLoading = true;
    },
    //START APPROVALS LOADING
    startApprovalsLoading(state) {
      state.isApprovalsLoading = true;
    },
    //START APPROVALS LOADING
    startSubmissionsLoading(state) {
      state.isSubmissionsLoading = true;
    },
    //GET ATTACHED SUPPLIERS SUCCESS
    getAttachedSuppliersSuccess(state, action) {
      state.isAttachedSuppliersLoading = false;
      state.attachedSuppliers = action.payload;
    },
    //GET SEARCH SUPPLIERS SUCCESS
    getSearchSuppliersSuccess(state, action) {
      state.isSearchSuppliersLoading = false;
      state.searchedSuppliers = action.payload;
    },
    //GET INVITED SUPPLIERS SUCCESS
    getInvitedSuppliersSuccess(state, action) {
      state.isSupplierInvitationLoading = false;
      state.supplierInvitations = action.payload;
    },
    //GET INVITATION URL SUCCESS
    getInvitationUrlSuccess(state, action) {
      state.isInvitationURLLoading = false;
      state.supplierInvitationURL = action.payload.registrationLink;
    },
    //GET APPROVALS SUCCESS
    getApprovalsSuccess(state, action) {
      state.isApprovalsLoading = false;
      state.supplierApprovals = action.payload;
    },
    //GET APPROVALS SUCCESS
    getSubmissionSuccess(state, action) {
      state.isSubmissionsLoading = false;
      state.dataSubmissions = action.payload;
    },
    //ADD SEARCH SUPPLIERS SUCCESS
    addInviteSuppliersSuccess(state, action) {
      state.invitedSuppliersCount = action.payload.NumberOfInvitationSent;
      state.addedSuppliersCount =
        action.payload.NumberOfExistingCustomerAttached;
      state.alreadyAttachedSuppliersCount =
        action.payload.NumberOfAlreadyAttachedCustomers;
    },
    //DELETE INTEGRATE REPORTS
    deleteAttachedSupplierSuccess(state, action) {
      state.isSupplierDeleteLoading = false;
    }
  }
});

export default slice.reducer;

// Actions
export const { addInviteSuppliersSuccess } = slice.actions;

// ----------------------------------------------------------------------

export function getAttachedSuppliers() {
  const isAlreadyAttached = true;
  return async (dispatch) => {
    dispatch(slice.actions.startAttachedSuppliersLoading());
    try {
      const response = await axios.get('/supplier', {
        params: {
          isAlreadyAttached
        }
      });
      dispatch(slice.actions.getAttachedSuppliersSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getSearchSuppliers(name) {
  const isActive = true;
  return async (dispatch) => {
    dispatch(slice.actions.startSearchSuppliersLoading());
    try {
      if (!checkUndefinedOrNull(name)) {
        const response = await axios.get('/supplier/find', {
          params: {
            name,
            isActive
          }
        });
        dispatch(slice.actions.getSearchSuppliersSuccess(response.data.data));
      } else {
        const response = await axios.get('/supplier');
        dispatch(slice.actions.getSearchSuppliersSuccess(response.data.data));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export async function addInviteSuppliers(data) {
  let supplierPayload = { ...data };
  try {
    const response = await axios.post(`/supplier`, {
      ...supplierPayload
    });
    return response;
  } catch (error) {
    return error;
  }
}

// ----------------------------------------------------------------------

export function deleteAttachedSupplier(id) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startDeleteSuppliersLoading());
      const response = await axios.delete(`/supplier/${id}`);
      dispatch(slice.actions.deleteAttachedSupplierSuccess({ id }));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

//-----------------------------------------------------------------------

export function getInvitedSuppliers() {
  return async (dispatch) => {
    dispatch(slice.actions.startInviteSuppliersLoading());
    try {
      const response = await axios.get('/supplier/manage/invited');
      dispatch(slice.actions.getInvitedSuppliersSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

//-----------------------------------------------------------------------

export function getCommonInvitationUrl() {
  return async (dispatch) => {
    dispatch(slice.actions.startInvitationUrlLoading());
    try {
      const response = await axios.get('/supplier/admin/invitation-url');
      dispatch(slice.actions.getInvitationUrlSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

//-----------------------------------------------------------------------

export function getSupplierApprovals() {
  return async (dispatch) => {
    dispatch(slice.actions.startApprovalsLoading());
    try {
      const response = await axios.get(
        '/supplier/admin/invitation-url/approvals'
      );
      dispatch(slice.actions.getApprovalsSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export async function postSupplierApprovals(data) {
  let approvalPayload = { ...data };
  try {
    const response = await axios.post(
      `/supplier/admin/invitation-url/approvals`,
      {
        ...approvalPayload
      }
    );
    return response;
  } catch (error) {
    return error;
  }
}

// ----------------------------------------------------------------------

export function getDataSubmissions() {
  return async (dispatch) => {
    dispatch(slice.actions.startSubmissionsLoading());
    try {
      const response = await axios.get('/supplier/admin/data-submissions');
      dispatch(slice.actions.getSubmissionSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
